/**
 * GrapesJS findPosition algorithm — direct port of
 * `packages/core/src/utils/sorter/SorterUtils.ts::findPosition` (line 15).
 *
 * Why we need it: our previous "top half / bottom half" drop detector was
 * fine for vertical lists but failed badly for flex rows, grids, and
 * absolutely-positioned elements. GrapesJS solves this with a single
 * algorithm that walks all sibling rectangles and uses x/y centers + limits
 * to short-circuit the search.
 *
 * Inputs:
 *   dims   — Array of {top, left, height, width, dir, el}
 *            `dir` is true for vertical-flow children (block/flex-column),
 *            false for horizontal-flow (float, flex-row, grid-row).
 *            We compute it ourselves in collectDims().
 *   posX   — cursor X (client coords)
 *   posY   — cursor Y (client coords)
 *
 * Output: { index: number, placement: 'before' | 'after' }
 *   index    — sibling index where the new element should be inserted
 *   placement— whether the indicator goes before or after that sibling
 */
export function findPosition(dims, posX, posY) {
  const result = { index: 0, placement: "before" };
  let leftLimit = 0;
  let xLimit = 0;
  let dimRight = 0;
  let yLimit = 0;
  let xCenter = 0;
  let yCenter = 0;
  let dimDown = 0;
  let dim;

  for (let i = 0, len = dims.length; i < len; i++) {
    dim = dims[i];
    const { top, left, height, width } = dim;
    dimRight = left + width;
    dimDown = top + height;
    xCenter = left + width / 2;
    yCenter = top + height / 2;
    if (
      (xLimit && left > xLimit) ||
      (yLimit && yCenter >= yLimit) ||
      (leftLimit && dimRight < leftLimit)
    ) continue;
    result.index = i;
    if (!dim.dir) {
      // Non-flow / horizontal lane — use x-center to decide before/after.
      if (posY < dimDown) yLimit = dimDown;
      if (posX < xCenter) {
        xLimit = xCenter;
        result.placement = "before";
      } else {
        leftLimit = xCenter;
        result.placement = "after";
      }
    } else {
      // Vertical flow — use y-center.
      if (posY < yCenter) {
        result.placement = "before";
        break;
      } else {
        result.placement = "after";
      }
    }
  }
  return result;
}

/**
 * Build a `dims` array from a list of sibling elements.
 * `dir=true` means vertical flow (block/flex-column), false means horizontal.
 * Detection mirrors GrapesJS's `isStyleInFlow` simplified for our DOM:
 * if the parent is `flex` with a horizontal `flex-direction` we treat the
 * children as horizontal; otherwise vertical.
 */
export function collectDims(parent, win) {
  const cs = win.getComputedStyle(parent);
  const isHorizontal =
    (cs.display === "flex" || cs.display === "inline-flex") &&
    !(cs.flexDirection || "").startsWith("column");
  // Grids: treat as horizontal if grid-auto-flow is row (default).
  const isGridRow = cs.display === "grid" && (cs.gridAutoFlow || "row").includes("row");
  const dir = !(isHorizontal || isGridRow);

  const kids = Array.from(parent.children).filter(c => c.getAttribute?.("data-sy-nodeid"));
  return kids.map(el => {
    const r = el.getBoundingClientRect();
    return {
      el,
      top: r.top, left: r.left, width: r.width, height: r.height,
      dir,
    };
  });
}
